import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';

const COLOR_PRESETS = [
  '#ffff00',
  '#00ff00',
  '#00ffff',
  '#ff00ff',
  '#0000ff',
  '#ff0000',
  '#ff8000',
  '#ff99cc',
  '#99ccff',
  '#008080',
  '#008000',
  '#800080',
  '#800000',
  '#808000',
  '#c0c0c0',
  '#808080',
  '#404040',
  '#000000',
];

type RgbColor = { r: number; g: number; b: number };
type HsvColor = { h: number; s: number; vibrance: number };
type RgbChannel = 'red' | 'green' | 'blue';

@Component({
  selector: 'lassi-color-picker',
  template: `
    <div
      #trigger
      class="color-picker"
      role="button"
      [attr.aria-disabled]="disabled"
      [attr.aria-expanded]="isOpen()"
      [attr.tabindex]="disabled ? -1 : 0"
      [class.disabled]="disabled"
      (click)="togglePanel()"
      (keydown)="handleTriggerKeydown($event)"
    >
      <fieldset>
        <legend>{{ label() }}</legend>
        <div class="swatch" [style.background]="value || '#ffffff'"></div>
      </fieldset>
    </div>

    <ng-template #panel>
      <div class="picker-panel">
        <div
          class="color-section"
          [style.background-color]="hueColor"
          (pointerdown)="handleColorSectionPointerDown($event)"
          (pointermove)="handleColorSectionPointerMove($event)"
          (pointerup)="handleColorSectionPointerUp($event)"
          (pointercancel)="handleColorSectionPointerUp($event)"
        >
          <div class="color-selection" [style]="colorSelectionStyle"></div>
        </div>

        <input
          class="hue-section"
          type="range"
          min="0"
          max="360"
          [value]="hue"
          (input)="onHueChange($any($event.target).value)"
        />

        <div class="rgb-section">
          <label>
            <input
              type="number"
              min="0"
              max="255"
              inputmode="numeric"
              [value]="red ?? ''"
              (input)="onRgbChange('red', $any($event.target).value)"
            />
            <span>R</span>
          </label>
          <label>
            <input
              type="number"
              min="0"
              max="255"
              inputmode="numeric"
              [value]="green ?? ''"
              (input)="onRgbChange('green', $any($event.target).value)"
            />
            <span>G</span>
          </label>
          <label>
            <input
              type="number"
              min="0"
              max="255"
              inputmode="numeric"
              [value]="blue ?? ''"
              (input)="onRgbChange('blue', $any($event.target).value)"
            />
            <span>B</span>
          </label>
        </div>

        <div class="presets-section">
          @for (preset of COLOR_PRESETS; track preset) {
            <button
              type="button"
              class="preset-button"
              [attr.aria-label]="'Select ' + preset"
              [style.background]="preset"
              (click)="selectColor(preset)"
            >
              <span class="visually-hidden">Select {{ preset }}</span>
            </button>
          }
        </div>

        <!-- <div class="actions">
          <button type="button" class="close-button" (click)="close()">
            Close
          </button>
        </div> -->
      </div>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .color-picker {
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
        min-width: 180px;
        user-select: none;

        fieldset {
          border-radius: 4px;

          legend {
            font-size: small;
          }
        }
      }

      .color-picker.disabled {
        opacity: 0.5;
        pointer-events: none;
      }

      .swatch {
        width: 100%;
        height: 16px;
        border-radius: 4px;
        border: 1px solid #d9d9d9;
      }

      .picker-panel {
        min-width: 280px;
        max-width: 320px;
        padding: 1rem;
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
        display: grid;
        gap: 0.5rem;
        grid-template-columns: 1fr auto;
        grid-template-rows: repeat(3, auto);
        grid-template-areas:
          'color hue'
          'inputs inputs'
          'presets presets';

        > {
          border-radius: 4px;
        }

        .color-section {
          grid-area: color;
          width: 100%;
          aspect-ratio: 1;
          position: relative;
          cursor: crosshair;
          touch-action: none;

          &::before {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to top, #000 0%, rgba(0, 0, 0, 0) 100%),
              linear-gradient(to right, #fff 0%, rgba(255, 255, 255, 0) 100%);
          }

          .color-selection {
            position: absolute;
            width: 12px;
            height: 12px;
            border: 2px solid #fff;
            border-radius: 50%;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.35);
            pointer-events: none;
          }
        }

        .hue-section {
          grid-area: hue;
          width: 24px;
          background: linear-gradient(
            0deg,
            red 0,
            #ff0 17%,
            #0f0 33%,
            #0ff 50%,
            #00f 67%,
            #f0f 83%,
            red
          );
          writing-mode: vertical-lr;
          direction: rtl;

          appearance: none;
          -webkit-appearance: none;
          overflow: visible;

          &:focus-visible {
            outline: 2px solid #1976d2;
            outline-offset: 3px;
          }

          &::-webkit-slider-runnable-track {
            background: transparent;
            border: 0;
          }

          &::-webkit-slider-thumb {
            appearance: none;
            -webkit-appearance: none;
            height: 14px;
            width: 32px;
            border-radius: 3px;
            background: white;
            cursor: pointer;
            margin-top: -5px;

            box-shadow:
              0 1px 3px 0 rgba(0, 0, 0, 0.1),
              0 1px 2px 4px rgba(0, 0, 0, 0.1);
          }

          &::-moz-range-track {
            background: transparent;
            border: 0;
          }

          &::-moz-range-thumb {
            appearance: none;
            height: 14px;
            width: 32px;
            border: 0;
            border-radius: 3px;
            background: white;
            cursor: pointer;
            box-shadow:
              0 1px 3px 0 rgba(0, 0, 0, 0.1),
              0 1px 2px 4px rgba(0, 0, 0, 0.1);
          }
        }

        .rgb-section {
          grid-area: inputs;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.5rem;
          text-align: center;

          label {
            display: grid;
            gap: 0.25rem;
            color: #333;
            font-size: 0.75rem;
            font-weight: 500;
          }

          span {
            line-height: 1;
          }

          input {
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            padding: 0.35rem 0.4rem;
            color: #333;
            font: inherit;
          }
        }

        .presets-section {
          margin-top: 0.5rem;
          grid-area: presets;
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 0.5rem;
          margin-bottom: 1rem;
        }
      }

      .preset-button {
        width: 100%;
        aspect-ratio: 1;
        padding: 0;
        border: none;
        border-radius: 4px;
        cursor: pointer;
      }

      .actions {
        display: flex;
        justify-content: flex-end;
      }

      .close-button {
        border: none;
        background: #f5f5f5;
        color: #333;
        padding: 0.5rem 0.9rem;
        border-radius: 4px;
        cursor: pointer;
      }

      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerComponent implements ControlValueAccessor, OnDestroy {
  readonly COLOR_PRESETS = COLOR_PRESETS;
  readonly label = input('Colour');

  private readonly panel = viewChild.required<TemplateRef<unknown>>('panel');
  private readonly trigger =
    viewChild.required<ElementRef<HTMLElement>>('trigger');

  disabled = false;
  private isSelectingColor = false;
  private color: HsvColor | null = null;

  get value(): string | null {
    if (!this.color) {
      return null;
    }

    return this.hsvToHex(this.color.h, this.color.s, this.color.vibrance);
  }

  get hue(): number {
    return this.currentColor().h;
  }

  get saturation(): number {
    return this.currentColor().s;
  }

  get vibrance(): number {
    return this.currentColor().vibrance;
  }

  get red(): number | null {
    return this.rgbValue()?.r ?? null;
  }

  get green(): number | null {
    return this.rgbValue()?.g ?? null;
  }

  get blue(): number | null {
    return this.rgbValue()?.b ?? null;
  }

  get colorSelectionStyle(): { left: string; top: string } {
    const x = this.saturation;
    const y = 100 - this.vibrance;
    return {
      left: `calc(${x}% - 6px)`,
      top: `calc(${y}% - 6px)`,
    };
  }

  get hueColor(): string {
    return this.hsvToHex(this.hue, 100, 100);
  }

  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly changeDetector = inject(ChangeDetectorRef);

  private overlayRef: OverlayRef | null = null;
  private readonly overlaySubscriptions = new Subscription();
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  togglePanel(): void {
    if (this.overlayRef?.hasAttached()) {
      this.close();
      return;
    }
    this.open();
  }

  open(): void {
    if (this.disabled) {
      return;
    }

    if (!this.panel() || !this.trigger()) {
      return;
    }

    const trigger = this.trigger().nativeElement;

    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        positionStrategy: this.getOverlayPosition(trigger),
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });

      this.overlaySubscriptions.add(
        this.overlayRef.backdropClick().subscribe(() => this.close()),
      );
      this.overlaySubscriptions.add(
        this.overlayRef.detachments().subscribe(() => this.onTouched()),
      );
    }

    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(
        new TemplatePortal(this.panel(), this.viewContainerRef),
      );
      this.changeDetector.markForCheck();
    }
  }

  close(): void {
    this.overlayRef?.detach();
    this.changeDetector.markForCheck();
  }

  selectColor(color: string): void {
    this.color = this.hexToHsv(color);
    this.propagateChange();
    this.onTouched();
    this.changeDetector.markForCheck();
  }

  onHueChange(rawValue: string): void {
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed)) {
      return;
    }

    this.color = { ...this.currentColor(), h: parsed };
    this.propagateChange();
    this.changeDetector.markForCheck();
  }

  handleColorSectionPointerDown(event: PointerEvent): void {
    if (this.disabled) {
      return;
    }

    this.isSelectingColor = true;
    event.preventDefault();
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    this.updateColorFromPointer(event);
  }

  handleColorSectionPointerMove(event: PointerEvent): void {
    if (!this.isSelectingColor) {
      return;
    }

    event.preventDefault();
    this.updateColorFromPointer(event);
  }

  handleColorSectionPointerUp(event: PointerEvent): void {
    if (!this.isSelectingColor) {
      return;
    }

    this.isSelectingColor = false;
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    this.onTouched();
  }

  writeValue(value: string | null): void {
    this.color = value ? this.hexToHsv(value) : null;
    this.changeDetector.markForCheck();
  }

  onRgbChange(channel: RgbChannel, rawValue: string): void {
    if (rawValue === '') {
      this.changeDetector.markForCheck();
      return;
    }

    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) {
      return;
    }

    const rgb = this.rgbValue() ?? { r: 0, g: 0, b: 0 };
    const value = Math.round(this.clamp(parsed, 0, 255));

    if (channel === 'red') {
      rgb.r = value;
    } else if (channel === 'green') {
      rgb.g = value;
    } else {
      rgb.b = value;
    }

    this.color = this.rgbToHsv(rgb.r, rgb.g, rgb.b);
    this.propagateChange();
    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.close();
    }
    this.changeDetector.markForCheck();
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: Event): void {
    if (this.overlayRef?.hasAttached()) {
      event.stopPropagation();
      this.close();
    }
  }

  @HostListener('document:pointerup')
  handleDocumentPointerUp(): void {
    this.isSelectingColor = false;
  }

  handleTriggerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.togglePanel();
  }

  ngOnDestroy(): void {
    this.overlaySubscriptions.unsubscribe();
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  isOpen(): boolean {
    return this.overlayRef?.hasAttached() ?? false;
  }

  private propagateChange(): void {
    this.onChange(this.value);
  }

  private updateColorFromPointer(event: PointerEvent): void {
    if (!(event.currentTarget instanceof HTMLElement)) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const x = this.clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const y = this.clamp((event.clientY - rect.top) / rect.height, 0, 1);

    this.color = {
      h: this.hue,
      s: Math.round(x * 100),
      vibrance: Math.round((1 - y) * 100),
    };
    this.propagateChange();
    this.changeDetector.markForCheck();
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private currentColor(): HsvColor {
    return this.color ?? { h: 0, s: 100, vibrance: 50 };
  }

  private rgbValue(): RgbColor | null {
    if (!this.color) {
      return null;
    }

    return this.hsvToRgb(this.color.h, this.color.s, this.color.vibrance);
  }

  private getOverlayPosition(trigger: HTMLElement): PositionStrategy {
    return this.overlay
      .position()
      .flexibleConnectedTo(trigger)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ])
      .withFlexibleDimensions(false)
      .withPush(false);
  }

  private hsvToRgb(h: number, s: number, v: number): RgbColor {
    h = ((h % 360) + 360) % 360;
    s /= 100;
    v /= 100;
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let r = 0;
    let g = 0;
    let b = 0;

    if (h >= 0 && h < 60) {
      r = c;
      g = x;
    } else if (h >= 60 && h < 120) {
      r = x;
      g = c;
    } else if (h >= 120 && h < 180) {
      g = c;
      b = x;
    } else if (h >= 180 && h < 240) {
      g = x;
      b = c;
    } else if (h >= 240 && h < 300) {
      r = x;
      b = c;
    } else {
      r = c;
      b = x;
    }

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
    };
  }

  private hsvToHex(h: number, s: number, v: number): string {
    const rgb = this.hsvToRgb(h, s, v);
    return this.rgbToHex(rgb.r, rgb.g, rgb.b);
  }

  private rgbToHex(r: number, g: number, b: number): string {
    const toHex = (value: number) =>
      Math.round(this.clamp(value, 0, 255))
        .toString(16)
        .padStart(2, '0');

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private rgbToHsv(r: number, g: number, b: number): HsvColor {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    let s = 0;
    const vibrance = max;

    if (delta !== 0) {
      s = max === 0 ? 0 : delta / max;

      switch (max) {
        case r:
          h = ((g - b) / delta + (g < b ? 6 : 0)) * 60;
          break;
        case g:
          h = ((b - r) / delta + 2) * 60;
          break;
        case b:
          h = ((r - g) / delta + 4) * 60;
          break;
      }
    }

    return {
      h: Math.round(h),
      s: Math.round(s * 100),
      vibrance: Math.round(vibrance * 100),
    };
  }

  private hexToHsv(
    hex: string,
  ): { h: number; s: number; vibrance: number } | null {
    const rgb = this.hexToRgb(hex);
    if (!rgb) {
      return null;
    }

    return this.rgbToHsv(rgb.r, rgb.g, rgb.b);
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const normalized = hex.replace('#', '');
    if (!/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalized)) {
      return null;
    }

    if (normalized.length === 3) {
      return {
        r: Number.parseInt(normalized[0] + normalized[0], 16),
        g: Number.parseInt(normalized[1] + normalized[1], 16),
        b: Number.parseInt(normalized[2] + normalized[2], 16),
      };
    }

    return {
      r: Number.parseInt(normalized.slice(0, 2), 16),
      g: Number.parseInt(normalized.slice(2, 4), 16),
      b: Number.parseInt(normalized.slice(4, 6), 16),
    };
  }
}
